// © Microsoft Corporation. All rights reserved.

import { AppError } from "@msnews/core-contracts";
import { Telemetry } from "@msnews/telemetry-contracts";

/**
 * Logs the given exception to telemetry.
 * @param error The error thrown.
 * @param appError The app error entry to log under.
 * @param message A message about what failed. This is what the error will be bucketed under for reporting purposes. The WHY will be included in the exception details.
 * @param customMessage details message for debug purpose
 */
export function logException(error: Error, appError: AppError, message: string, customMessage?: string): void {
    const errorObj = createError(appError, message, customMessage || error && error.toString());
    errorObj.pb.stack = error && error.stack;
    Telemetry.sendAppErrorEvent(errorObj);
}

/**
 * Helper that updates the property bag message and then fires the app error
 * @param appError The apperror
 * @param message The message for the error
 * @param customMessage The custom message to add to the property bag
 */
export function logError(appError: AppError, message: string, customMessage?: string): void {
    Telemetry.sendAppErrorEvent(createError(appError, message, customMessage));
}

/**
 * Helper to create an error for logging with message and customMessage properties without having
 * to go manually clone the error object, or property bag, etc.
 * @param baseError - The base error object to use as prototype for creating the error
 * @param message - The custom message to use for the error
 * @param customMessage - The custom message that may contain strings that do not aggregate well (i.e., unique / per request data)
 */
export function createError(baseError: AppError, message: string, customMessage?: string): AppError {
    return {
        ...baseError,
        message: message || baseError.message,
        pb: customMessage
            ? { ...baseError.pb, customMessage }
            : baseError.pb
    };
}